import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EntityService } from '../services/entity.service';
import { EmployeeService } from '../services/employee.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { Employee } from '../shared/models/employee';
import { EmployeeActionComponent } from './employee-action.component';
import { IEntity } from '../shared/models/entity';
import { EmployeeFormComponent } from './employee-form.component';
import { forkJoin } from 'rxjs';
import { EmployeeActivityFormComponent } from './employee-activity-form.component';
import { EmployeeActivityModel } from '../shared/models/employee-activity-model';
import { ToastService } from '../shared/toast/toast.service';
import { AuthenticationService } from '../services/authentication.service';
import { BusinessOperation } from '../shared/models/business-operation';
import { Router } from '@angular/router';
import { Position } from '../shared/models/position';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuItem } from 'primeng/api';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { SubSink } from 'subsink';
import { RosterTypeService } from '../services/roster-type.service';
import { IRosterType } from '../shared/models/roster/roster-type';
import { IWorkArea } from '../shared/models/workarea';
import { WorkAreaService } from '../services/work-area.service';
import { EmployeeTrainingRecordService } from '../services/employee-training-record.service';
import { EmployeeTrainingRecordWarningComponent } from './employee-training-record-warning.component';
import { EmployerService } from '../services/employer.service';
import { IEmployer } from '../shared/models/employer';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'pps-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EmployeesComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen = false;

  items: MenuItem[] = [{label: "Employee", icon: "pi-fw far fa-address-card"}];
  spinnerName: string = "employeeSpinner";
  
  clientCode: string = environment.clientCode;

  isLoggedIn: boolean = false;
  exporting: boolean = false;
  gridHeaders = [];
  context:any = {};
  employees: Employee[] = [];
  gridOptions = {
    getRowId: param => `${param.data.id}`,
    defaultColDef: {
      cellStyle: this.getCellStyle,
      resizable: true,
      floatingFilter: true
    }
  };

  selectedRow: any = null;
  selectedRowNode: any = null; //ag-grid RowNode

  private gridApi;
  private groups: IEntity[] = [];
  private positions: Position[] = [];
  private rosterTypes: IRosterType[] = [];
  private workAreas: IWorkArea[] = [];
  private employers: IEmployer[] = [];

  private dateFilterParams = {
    comparator: this._util.filterDateComparator,
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "greaterThan"
  };

  constructor(private _dialog: DialogService,
              private _spinner: NgxSpinnerService,
              private _employeeService: EmployeeService,
              private _employerService: EmployerService,
              private _emplyoeeTrainingRecordService: EmployeeTrainingRecordService,
              private _entityService: EntityService,
              private _toast: ToastService,
              private _authService: AuthenticationService,
              private _rosterTypeService: RosterTypeService,
              private _workAreaService: WorkAreaService,
              private _router: Router,
              private _util: PpsUtilityService,
              private breakpointObserver: BreakpointObserver) { 
    this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });      
    }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadEntities();
  }

  onGridReady(params) {
    this.gridApi = params.api;

    if (!this.canModifyEmployee()){
      params.api.setColumnsVisible(['action'], false);
    }
  }

  onRowClicked(evt){
    let _this = evt.context.componentParent;
    var selectedRows = _this.gridApi.getSelectedRows();
    _this.selectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
    _this.selectedRowNode = evt.node;
  }

  onRowDblClicked(params){
    if (this.canModifyEmployee()){
      let employee = params.data;
      this.openEmployeeForm(employee);
    }
  }

  // load groups and employers
  loadEntities(){
    this._spinner.show(this.spinnerName);
    this.subs.sink = forkJoin([
      this._entityService.getGroups(),
      this._entityService.getPositions(),
      this._rosterTypeService.getAll(),
      this._workAreaService.getAll(),
      this._employerService.getAll()
    ]).subscribe(([groupResponse, positionsResponse, rosterTypesRes, waRes, employerRes]) => { // onNext
      if (groupResponse.succeed){
        this.groups = groupResponse.result;
      }else{
        this._toast.failed("Load group failed! " + groupResponse.errors.join("|"));
      }

      if (positionsResponse.succeed){
        this.positions = positionsResponse.result;
      }else{
        this._toast.failed("Load positions failed! " + positionsResponse.errors.join("|"));
      }

      if (rosterTypesRes.succeed){
        this.rosterTypes = rosterTypesRes.result;
      }else{
        this._toast.failed("Load roster types failed! " + rosterTypesRes.errors.join("|"));
      }

      if (waRes.succeed){
        this.workAreas = waRes.result;
      }else{
        this._toast.failed("Load work areas failed! " + waRes.errors.join("|"));
      }

      if (employerRes.succeed){
        this.employers = employerRes.result;
      }else{
        this._toast.failed("Load company data failed! " + employerRes.errors.join("|"));
      }
    }, error => { // onError
      if (error.status === 0){
        this._router.navigate(["/"], { replaceUrl: true});
      }
    }, () => {  //onComplete
      this.loadEmployees();
    }).add(() => { //Finalize handler
      this._spinner.hide(this.spinnerName);
    });
  }

  loadEmployees(){
    this._spinner.show(this.spinnerName);
    this.employees = [];
    this.subs.sink = this._employeeService
      .getEmployees(false)
      .subscribe(response => {
        if (response.succeed){
          this.employees = response.result;
          this._toast.succeeded(`${this.employees.length} employees have been loaded`);
        }else{
          this._toast.failed("Loading employee failed: " + response.errors.join(";"));
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }

  openEmployeeForm(employee: Employee){
    if (employee === null){
      employee = new Employee();
      employee.id = 0;
      employee.showInRoster = true;
    }

    let dialog = this._dialog.open(EmployeeFormComponent, {
      header: employee.id === 0 ? "Add Employee" : "Edit Employee", 
      styleClass: "size-md",
      data:{
        employee: employee,
        employees: this.employees,
        workGroups: this.groups,
        positions: this.positions,
        rosterTypes: this.rosterTypes,
        workAreas: this.workAreas,
        employers: this.employers
      },
      draggable: true
    });


    this.subs.sink = dialog.onClose.subscribe(employee => {
      if (employee){
        this.save(employee);
      }
    });
  }

  save(employee: Employee){
    if (employee.id === 0){
      employee.active = true;
      this.subs.sink = this._employeeService
        .addEmployee(employee)
        .subscribe(response => {
          if (response.succeed){
            this.employees.unshift(response.result);
            this._toast.succeeded("Employee create successfully");
            this.gridApi.setGridOption('rowData', this.employees);
          }else{
            this._toast.failed("Employee creatation failed: " + response.errors.join(";"));
          }
        });
    }else{
      this.subs.sink = this._employeeService
        .updateEmployee(employee)
        .subscribe(response => {
          if (response.succeed){
            var foundIndex = this.employees.findIndex(x => x.id == response.result.id);
            this.employees[foundIndex] = response.result;
            this.resetGrid(this.employees, employee.id, false);
            this._toast.succeeded("Employee updated successfully");
          }else{
            this._toast.failed("Employee update failed: " + response.errors.join(";"));
          }
        });
    }
  }

  checkEmployeeTrainingRecord(employee: Employee, callback){
    this.subs.sink = this._emplyoeeTrainingRecordService
    .getTrainingRecordInPast6Month(employee.id)
    .subscribe(response => {
      if (response.succeed && response.result.length > 0){
        console.log(response.result);
        let dialog = this._dialog.open(EmployeeTrainingRecordWarningComponent, {
          header: "Warning: Training Records Found in the Past 6 Months",
          styleClass: "size-md",
          data:{
            employeeTrainingRecords: response.result
          },
          draggable: true
        });

        this.subs.sink = dialog.onClose.subscribe( () => {
          if (callback){
            callback();
          }
        });
      }else{
        if (callback){
          callback();
        }
      }
    });
  }

  activateEmployee(employee: Employee, active: boolean){
    if (employee === null){
      return;
    }

    if (!active){
      this.checkEmployeeTrainingRecord(employee, () => {
        this.activateEmployeeHelper(employee, active);
      })
    }else{
      this.activateEmployeeHelper(employee, active);
    }
  }

  activateEmployeeHelper(employee: Employee, active: boolean){
    let activity = active ? "Activate Employee" : "Terminate Employee";

    let dialog = this._dialog.open(EmployeeActivityFormComponent, {
      header: activity,
      styleClass: "size-md",
      focusOnShow: false,
      data:{
        employee: employee,
        activity: activity,
        title: activity
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      employeeActivityModel => {
        if (employeeActivityModel){
          this.saveEmployeeActivityModel(employee, employeeActivityModel, active);
        }
      },
      reason => {
        console.log(reason);
      }
    );
  }

  exportEmployee(){
    this.exporting = true;
    this.subs.sink = this._employeeService.downloadEmployees(() => this.exporting = false);
  }

  private saveEmployeeActivityModel(employee: Employee, empActivityModel: EmployeeActivityModel, active: boolean){
    this.subs.sink = this._employeeService.activateEmployee(empActivityModel, active)
    .subscribe(res => {
      if (res.succeed){
        this._toast.succeeded(`${this._util.getEmployeeName(employee)} has been ${active ? "activated" : "terminated"}!`)
        this.loadEmployees();
      }else{
        this._toast.failed(`${active ? "Activate" : "Termiate"} employee failed: ` + res.errors.join(";"));
      }
    })
  }

  getFilteredCount(){
    if (this.gridApi){
      return this.gridApi.getModel().rootNode.childrenAfterFilter.length;
    }

    return 0;
  }

  getTotalActiveEmployeeCount(){
    var arr = this.employees.filter(e=>e.active && e.dateOfBirth && e.employer?.employerName?.toLowerCase() === environment.clientCode);
    return arr.length;
  }

  canAddEmployee(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.AddEmployee);
  }

  canModifyEmployee(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ModifyEmployee);
  }

  canExportEmployee(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ExportEmployee);
  }

  canViewEmployeeRosterType(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageRosterSetting);
  }

  private getColumnDefs(){
    let colDefs = [
      {
        headerName: "Action",
        field: "action",
        cellRenderer: EmployeeActionComponent,
        colId: "action",
        width: 80,
        hide: false,
        pinned: true,
        suppressMovable: true,
        resizable: false,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Full Name',
        field:'fullname',
        filter: true,
        sortable: true,
        pinned: true,
        width: 150,
        suppressMovable: true,
        valueGetter: this.getEmployeeFullname
      },
      {
        headerName: 'Payroll No',
        field:'employeeNo',
        filter: true,
        sortable: true,
        width: 100
      },
      {
        headerName: 'Group',
        field:'workGroup.name',
        filter: true,
        sortable: true,
        width: 300
      },
      {
        headerName: 'Position',
        field: 'position.title',
        filter: true,
        sortable: true,
        width: 150
      },
      {
        headerName: 'Supervisor',
        field:'',
        filter: true,
        sortable: true,
        width: 200,
        valueGetter: this.getSupervisorName
      },
      {
        headerName: 'Access Id',
        field:'accessId',
        filter: true,
        sortable: true,
        width: 100
      },
      {
        headerName: 'Email',
        field:'email',
        filter: true,
        sortable: true,
        width: 200
      },
      {
        headerName: 'Phone',
        field:'phone',
        filter: true,
        sortable: true,
        width: 160
      },
      {
        headerName: 'Date of Birth',
        field:'dateOfBirth',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        sortable: true,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        width: 170,
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Hire Date',
        field:'hireDate',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        sortable: true,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        width: 170,
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Contract Expire Date',
        field:'contractExpireDate',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        sortable: true,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        width: 170,
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Company',
        field:'employer.employerName',
        filter: true,
        sortable: true,
        width: 100
      },
      {
        headerName: 'Home Address',
        field:'',
        filter: true,
        sortable: true,
        resizable:true,
        width: 300,
        valueGetter: this.getEmployeeHomeAddress
      },
      {
        headerName: 'Active Employee',
        field:'active',
        filter: true,
        sortable: true,
        valueGetter: params => params.data[params.colDef.field] ? "Yes" : "No",
        cellStyle: this.getCellStyle,
        width: 150
      },
      {
        headerName: 'Terminate Date',
        field:'terminateDate',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        sortable: true,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        width: 170,
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Terminate Note',
        field:'terminateNote',
        filter: true,
        sortable: false,
        width: 300
      },
      {
        headerName: 'Id',
        field:'id',
        filter: "agNumberColumnFilter",
        sortable: true,
        width: 50,
        floatingFilter: false,
        resizable: false,
        cellStyle: {textAlign:"center"}
      }
    ];

    if (this.canViewEmployeeRosterType){
      colDefs.splice(7, 0, 
        {
          headerName: 'Work Area',
          field:'workArea.areaName',
          filter: true,
          sortable: true,
          width: 120
        },
        {
          headerName: 'Roster Type',
          field:'rosterType.title',
          filter: true,
          sortable: true,
          width: 150
        },
        {
          headerName: 'Roster Type Start Date',
          field:'rosterTypeStartDate',
          filter: 'agDateColumnFilter',
          filterParams: this.dateFilterParams,
          sortable: true,
          comparator: (date1, date2) => this._util.dateComparator(date1, date2),
          width: 170,
          valueGetter: this.getDateValue
        });
    }

    if (this.smallScreen){
      colDefs.splice(0, 1);
      colDefs[0].pinned = false;
    }

    return colDefs;
  }

  private getSupervisorName(params){
    let rowData = params.data;
    return rowData.supervisor ? params.context.componentParent._util.getEmployeeName(rowData.supervisor) : "";
  }
  
  private getEmployeeFullname(params){
    return params.context.componentParent._util.getEmployeeName(params.data);
  }

  private getEmployeeHomeAddress(params){
    let rowData = params.data;
    return [rowData.address, rowData.city, rowData.state, rowData.postcode].join(" ");
  }

  private getCellStyle(params){
    let employee: Employee = params.data;

    if (!employee.active){
      return {color: "#aaaaaa"};
    }
  }

  //
  // This is to update the values of the project grid and make sure the selected
  // row is rendered correctly
  private resetGrid(gridData: any, selectedId: number, ensureVisible: boolean){
    // reference:
    // https://stackoverflow.com/questions/55723337/ag-grid-how-to-scroll-to-last-known-position
    // if ( ensureVisible ){
    //   this.gridOptions.immutableData = false;
    // }

    this.gridApi.setGridOption('rowData', gridData);
    
    if (selectedId !== 0 && ensureVisible){
      var node = this.gridApi.getRowNode(selectedId);
      if (node){
        this.gridApi.ensureIndexVisible(node.rowIndex, 'middle');
        node.setSelected(true);
      }
    }

    // if ( ensureVisible ){
    //   this.gridOptions.immutableData = true;
    // }
  }

  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }
}
